import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { fetchPlaceCategory, deletePlaceCategory } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function PlaceCategoryDetailPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    try { setData(await fetchPlaceCategory(categoryId)); }
    catch (e) { notify.apiError(e); }
  }, [categoryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deletePlaceCategory(categoryId);
      notify.success(t("message.delete.success"));
      navigate(`/strategy/${businessDomainId}/place-categories`, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  const typeNames = (data.associatedPlaceTypes || [])
    .map((pt) => pt.placeTypeName).join(", ");

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("placeCategory.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`/strategy/${businessDomainId}/place-categories/${categoryId}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>
      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("placeCategory.name")} value={data.placeCategoryName} /></Grid>
          <Grid size={12}><DetailField label={t("placeCategory.placeTypes")} value={typeNames} /></Grid>
          <Grid size={12}><DetailField label={t("common.description")} value={data.placeCategoryDescription} /></Grid>
        </Grid>
      </DetailSection>
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<AssessmentIcon />}
          onClick={() => navigate(`/strategy/${businessDomainId}/place-categories/${categoryId}/classification-criteria`)}>
          {t("placeCategory.classificationCriteriaValues")}
        </Button>
      </Box>
      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/place-categories`)}>{t("common.back")}</Button>
      </Box>
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
    </Box>
  );
}
